import { useParams } from "react-router-dom";
import DatasetTableResponse from "datasets/pages/tables/types/interfaces/table";
import queryString from "query-string";
import { KeyedMutator, SWRResponse } from "swr";
import { NumberParam, useQueryParams, withDefault } from "use-query-params";
import { useDebounce } from "~/hooks/useDebounce";
import { useFetcher } from "~/hooks/useFetcher";
import SearchResponse, { TableEntity } from "~/types/interfaces/catalogResponse";

const { VITE_CATALOG_API } = import.meta.env;

export const PAGE_SIZE = 10;

const adaptHit = ({ _source }: { _source: TableEntity }): DatasetTableResponse => ({
  id: _source.id,
  name: _source.name,
  description: _source.description ?? "",
  fullyQualifiedName: _source.fullyQualifiedName,
  database_schema: _source.databaseSchema.name,
  updated_at: new Date(_source.updatedAt).toISOString(),
  columns: _source.columns,
  tableType: _source.tableType,
  deleted: false,
});

// Shape of a row from GET /tables/dataset/{id}, which is always up-to-date for a given dataset.
// The endpoint serializes with FastAPI's by_alias default, so it emits `fullyQualifiedName`
// and `deleted` (not the Python-side `fqn`/`is_deleted` field names). Only the fields the
// adapter reads are declared here.
interface DatasetTableRow {
  id: string;
  name: string;
  description?: string;
  fullyQualifiedName: string;
  database_schema: string;
  updated_at: string;
  tableType?: string;
  deleted: boolean;
  columns: { name: string; fullyQualifiedName: string }[];
}

const adaptTable = (row: DatasetTableRow): DatasetTableResponse => ({
  id: row.id,
  name: row.name,
  description: row.description ?? "",
  fullyQualifiedName: row.fullyQualifiedName,
  database_schema: row.database_schema,
  updated_at: row.updated_at,
  columns: row.columns.map((c) => ({
    name: c.name,
    fullyQualifiedName: c.fullyQualifiedName,
  })),
  tableType: row.tableType,
  deleted: row.deleted,
});

// Select the adapter by the shape of the data payload: with
// `keepPreviousData`, the previous branch's payload can linger while the SWR key
// switches, so for a period the payload shape may not match the query
const adaptResponse = (
  data: SearchResponse<TableEntity> | DatasetTableRow[] | undefined
): DatasetTableResponse[] | undefined => {
  if (!data) {
    return undefined;
  }
  return Array.isArray(data) ? data.map(adaptTable) : data.hits.hits.map(adaptHit);
};

const useDatasetTables = (
  searchTerm: string = "",
  id?: string
): SWRResponse<DatasetTableResponse[]> => {
  const { datasetId = id } = useParams();
  const debouncedSearch = useDebounce(searchTerm);
  const [query] = useQueryParams({
    perPage: withDefault(NumberParam, PAGE_SIZE),
    page: withDefault(NumberParam, 0),
  });
  const { page, perPage } = query;
  const offset = page ? (page - 1) * perPage : 0;

  const searchQs = queryString.stringify({
    index: "catalog-api-table_search_index",
    dataset_id: datasetId,
    q: debouncedSearch,
    query_filter: JSON.stringify({ query: { match_all: {} } }),
    from: offset,
    size: perPage,
  });

  // No search term: read the always-current Postgres endpoint so freshly created
  // or removed tables show up immediately, side-stepping the OpenSearch index lag.
  // When a search term is present, use the OpenSearch index for its full-text search capabilities.
  // The OpenSearch index is eventually consistent, with up to 5-minute lag as of this writing.
  const listQs = queryString.stringify({
    offset,
    limit: perPage,
    sort_column: "name",
    sort_order: "asc",
  });

  const key = datasetId
    ? debouncedSearch
      ? `${VITE_CATALOG_API}/search/query?${searchQs}`
      : `${VITE_CATALOG_API}/tables/dataset/${datasetId}?${listQs}`
    : null;

  const resp = useFetcher<SearchResponse<TableEntity> | DatasetTableRow[]>(key, {
    keepPreviousData: true,
  });

  return {
    ...resp,
    data: adaptResponse(resp.data),
    isLoading: resp.isLoading || Boolean(searchTerm && searchTerm !== debouncedSearch),
    mutate: resp.mutate as KeyedMutator<any>,
  } as SWRResponse<DatasetTableResponse[]>;
};

export default useDatasetTables;
